import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../db/shiksha-db';
import { StatusBar } from '../../components/StatusBar';
import {
  Users,
  Settings,
  FileSpreadsheet,
  FileText,
  Activity,
  HardDrive,
  ArrowRightLeft
} from 'lucide-react';
import { TutorialTour } from '../../components/TutorialTour';

/**
 * AdminDashboard — The control center for the Principal/IT Lead.
 */
export function AdminDashboard() {
  const { user } = useAuth();
  
  const studentCount = useLiveQuery(() => db.students.count()) || 0;
  const teacherCount = useLiveQuery(() => db.teachers.count()) || 0;
  const marksCount = useLiveQuery(() => db.marks.count()) || 0;
  const [storageUsage, setStorageUsage] = useState({ usage: 0, quota: 100 });
  const navigate = useNavigate();

  useEffect(() => {
    // DPDP Consent Check
    const checkConsent = async () => {
      if (user?.email) {
        const consent = await db.dpdpConsent.where('adminEmail').equals(user.email).first();
        if (!consent || consent.status !== 'agreed') {
          navigate('/admin/consent', { replace: true });
        }
      }
    };
    checkConsent();
  }, [user, navigate]);

  useEffect(() => {
    if (navigator.storage && navigator.storage.estimate) {
      navigator.storage.estimate().then(estimate => {
        const usageMB = (estimate.usage / (1024 * 1024)).toFixed(2);
        const quotaMB = (estimate.quota / (1024 * 1024)).toFixed(0);
        setStorageUsage({ usage: usageMB, quota: quotaMB });
      });
    }
  }, []);

  const adminName = user?.displayName || user?.email?.split('@')[0] || 'Admin';
  
  const storagePercent = storageUsage.quota > 0 
    ? ((storageUsage.usage / storageUsage.quota) * 100).toFixed(1) 
    : 0;

  return (
    <div className="min-h-screen bg-paper pb-8">
      <TutorialTour type="admin" />
      {/* ── Welcome Banner ── */}
      <div className="bg-indigo-900 text-white">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold tracking-tight mb-2">System Admin, {adminName}</h1>
          <p className="text-indigo-200 font-secondary">Control Center & System Health Overview.</p>
        </div>
      </div>

      {/* ── System Health ── */}
      <div id="tour-admin-stats" className="max-w-6xl mx-auto px-4 -mt-8 mb-8">
        <div className="bg-white border border-divider rounded-xl p-5 shadow-sm grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 divide-y sm:divide-y-0 sm:divide-x divide-divider">
          <div className="flex flex-col pt-4 sm:pt-0 sm:px-4 first:pt-0 first:pl-0">
            <div className="flex items-center gap-2 text-green-600 mb-2">
              <Activity size={18} />
              <span className="text-sm font-semibold font-secondary">System API</span>
            </div>
            <p className="text-2xl font-bold text-ink">{navigator.onLine ? 'Online' : 'Offline'}</p>
            <p className="text-xs text-graphite mt-1 font-secondary">Network Status</p>
          </div>
          <div className="flex flex-col pt-4 sm:pt-0 sm:px-4">
            <div className="flex items-center gap-2 text-indigo-600 mb-2">
              <HardDrive size={18} />
              <span className="text-sm font-semibold font-secondary">Local Storage</span>
            </div>
            <p className="text-2xl font-bold text-ink">{storagePercent}%</p>
            <p className="text-xs text-graphite mt-1 font-secondary">{storageUsage.usage} MB used</p>
          </div>
          <div className="flex flex-col pt-4 sm:pt-0 sm:px-4">
            <div className="flex items-center gap-2 text-amber-600 mb-2">
              <Users size={18} />
              <span className="text-sm font-semibold">Students & Staff</span>
            </div>
            <p className="text-2xl font-bold text-ink">{studentCount}</p>
            <p className="text-xs text-graphite mt-1">{teacherCount} Teachers Synced</p>
          </div>
          <div className="flex flex-col pt-4 sm:pt-0 sm:px-4">
            <div className="flex items-center gap-2 text-blue-600 mb-2">
              <FileSpreadsheet size={18} />
              <span className="text-sm font-semibold">Stored Marks</span>
            </div>
            <p className="text-2xl font-bold text-ink">{marksCount}</p>
            <p className="text-xs text-graphite mt-1">Local database records</p>
          </div>
        </div>
      </div>

      {/* ── Action Cards ── */}
      <div className="max-w-6xl mx-auto px-4">
        <h2 className="text-lg font-semibold text-ink mb-4">Administration Tools</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          
          <Link
            id="tour-manage-teachers"
            to="/admin/users"
            className="group flex flex-col bg-white border border-divider rounded-xl p-5 hover:border-action hover:shadow-sm transition-all duration-200"
          >
            <div className="w-12 h-12 rounded-lg bg-indigo-50 flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
              <Users size={24} className="text-indigo-600 group-hover:text-white" />
            </div>
            <h3 className="text-lg font-bold text-ink mb-1">Manage Teachers</h3>
            <p className="text-sm text-graphite font-secondary">Add, remove, or assign domains to teaching staff.</p>
          </Link>

          <Link
            id="tour-udise-ingest"
            to="/admin/udise"
            className="group flex flex-col bg-white border border-divider rounded-xl p-5 hover:border-action hover:shadow-sm transition-all duration-200"
          >
            <div className="w-12 h-12 rounded-lg bg-green-50 flex items-center justify-center mb-4 group-hover:bg-green-600 group-hover:text-white transition-colors">
              <FileSpreadsheet size={24} className="text-green-600 group-hover:text-white" />
            </div>
            <h3 className="text-lg font-bold text-ink mb-1">UDISE Ingestion</h3>
            <p className="text-sm text-graphite font-secondary">Upload and map UDISE+ CSVs to the system ledger.</p>
          </Link>

          <Link
            to="/admin/schema"
            className="group flex flex-col bg-white border border-divider rounded-xl p-5 hover:border-action hover:shadow-sm transition-all duration-200"
          >
            <div className="w-12 h-12 rounded-lg bg-amber-50 flex items-center justify-center mb-4 group-hover:bg-amber-600 group-hover:text-white transition-colors">
              <Settings size={24} className="text-amber-600 group-hover:text-white" />
            </div>
            <h3 className="text-lg font-bold text-ink mb-1">Marking Schema</h3>
            <p className="text-sm text-graphite font-secondary">Visual AST builder for calculation formulas.</p>
          </Link>

          <Link
            id="tour-report-gen"
            to="/admin/reports"
            className="group flex flex-col bg-white border border-divider rounded-xl p-5 hover:border-action hover:shadow-sm transition-all duration-200"
          >
            <div className="w-12 h-12 rounded-lg bg-purple-50 flex items-center justify-center mb-4 group-hover:bg-purple-600 group-hover:text-white transition-colors">
              <FileText size={24} className="text-purple-600 group-hover:text-white" />
            </div>
            <h3 className="text-lg font-bold text-ink mb-1">Generate Reports</h3>
            <p className="text-sm text-graphite font-secondary">Batch generate student report cards (CBSE A4 layout).</p>
          </Link>

          <Link
            to="/admin/settings"
            className="group flex flex-col bg-white border border-divider rounded-xl p-5 hover:border-action hover:shadow-sm transition-all duration-200"
          >
            <div className="w-12 h-12 rounded-lg bg-slate-50 flex items-center justify-center mb-4 group-hover:bg-slate-700 group-hover:text-white transition-colors">
              <Settings size={24} className="text-slate-600 group-hover:text-white" />
            </div>
            <h3 className="text-lg font-bold text-ink mb-1">Settings</h3>
            <p className="text-sm text-graphite font-secondary">School name, Google Sheet links, academic year.</p>
          </Link>

          <Link
            id="tour-rollover"
            to="/admin/rollover"
            className="group flex flex-col items-center justify-center bg-rose-600 border border-transparent rounded-xl p-6 hover:bg-rose-700 shadow-lg hover:shadow-xl transition-all duration-300 md:col-span-2 lg:col-span-4"
          >
            <div className="w-16 h-16 rounded-full bg-rose-500 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <ArrowRightLeft size={32} className="text-white" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2 tracking-wide">START NEW ACADEMIC YEAR</h3>
            <p className="text-rose-100 font-secondary text-center max-w-lg">
              Automatically bump student classes, create a new Academic Year Google Sheet with populated Class Tabs, and secure the ledger.
            </p>
          </Link>

        </div>
      </div>
    </div>
  );
}
